'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface FilterOptions {
  category: string;
  priceRange: [number, number];
  location: string;
  condition: string;
  manufacturer: string;
  yearRange: [number, number];
  keyword: string;
}

interface FilterPanelProps {
  filters: FilterOptions;
  onFiltersChange: (filters: FilterOptions) => void;
  resultCount: number;
  isMobile?: boolean;
  isOpen?: boolean;
  onClose?: () => void;
}

const FilterPanel = ({ 
  filters, 
  onFiltersChange, 
  resultCount, 
  isMobile = false, 
  isOpen = true, 
  onClose 
}: FilterPanelProps) => {
  const [localFilters, setLocalFilters] = useState<FilterOptions>(filters);

  const categories = [
    'All Categories',
    'Excavators',
    'Bulldozers',
    'Cranes',
    'Loaders',
    'Dump Trucks',
    'Compactors',
    'Graders',
    'Scrapers'
  ];

  const conditions = ['All Conditions', 'New', 'Used - Excellent', 'Used - Good', 'Used - Fair'];
  const manufacturers = ['All Manufacturers', 'Caterpillar', 'Komatsu', 'John Deere', 'Volvo', 'Liebherr', 'Hitachi', 'JCB'];
  const locations = ['All Locations', 'California', 'Texas', 'Florida', 'New York', 'Illinois', 'Pennsylvania', 'Ohio'];

  const handleFilterChange = (key: keyof FilterOptions, value: any) => {
    const updatedFilters = { ...localFilters, [key]: value };
    setLocalFilters(updatedFilters);
    onFiltersChange(updatedFilters);
  };

  const handlePriceRangeChange = (index: number, value: number) => {
    const newRange: [number, number] = [...localFilters.priceRange] as [number, number];
    newRange[index] = value;
    handleFilterChange('priceRange', newRange);
  };

  const handleYearRangeChange = (index: number, value: number) => {
    const newRange: [number, number] = [...localFilters.yearRange] as [number, number];
    newRange[index] = value;
    handleFilterChange('yearRange', newRange);
  };

  const clearAllFilters = () => {
    const defaultFilters: FilterOptions = {
      category: 'All Categories',
      priceRange: [0, 1000000],
      location: 'All Locations',
      condition: 'All Conditions',
      manufacturer: 'All Manufacturers',
      yearRange: [1990, 2024],
      keyword: ''
    };
    setLocalFilters(defaultFilters);
    onFiltersChange(defaultFilters);
  };

  const panelContent = (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-lg font-bold uppercase tracking-wide text-foreground">
            Filter Equipment
          </h2>
          <p className="text-sm text-text-secondary mt-1">
            {resultCount.toLocaleString()} results found
          </p>
        </div>
        {isMobile && (
          <button
            onClick={onClose}
            className="p-2 rounded-industrial hover:bg-muted transition-industrial"
          >
            <Icon name="XMarkIcon" size={20} />
          </button>
        )}
      </div>

      {/* Search Keyword */}
      <div>
        <label className="block text-sm font-heading font-bold uppercase tracking-wide text-foreground mb-2">
          Search
        </label>
        <div className="relative">
          <input
            type="text"
            value={localFilters.keyword}
            onChange={(e) => handleFilterChange('keyword', e.target.value)}
            placeholder="Search equipment..."
            className="w-full pl-10 pr-4 py-2 border border-border rounded-industrial focus:ring-2 focus:ring-ring focus:border-transparent"
          />
          <Icon name="MagnifyingGlassIcon" size={16} className="absolute left-3 top-3 text-text-secondary" />
        </div>
      </div>

      {/* Category */}
      <div>
        <label className="block text-sm font-heading font-bold uppercase tracking-wide text-foreground mb-2">
          Category
        </label>
        <select
          value={localFilters.category}
          onChange={(e) => handleFilterChange('category', e.target.value)}
          className="w-full p-2 border border-border rounded-industrial focus:ring-2 focus:ring-ring focus:border-transparent"
        >
          {categories.map((category) => (
            <option key={category} value={category}>
              {category}
            </option>
          ))}
        </select>
      </div>

      {/* Price Range */}
      <div>
        <label className="block text-sm font-heading font-bold uppercase tracking-wide text-foreground mb-2">
          Price Range
        </label>
        <div className="space-y-3">
          <div className="flex items-center space-x-2 min-w-0">
            <input
              type="number"
              value={localFilters.priceRange[0]}
              onChange={(e) => handlePriceRangeChange(0, parseInt(e.target.value) || 0)}
              placeholder="Min"
              className="w-0 flex-1 min-w-0 p-2 border border-border rounded-industrial focus:ring-2 focus:ring-ring focus:border-transparent text-sm"
            />
            <span className="text-text-secondary flex-shrink-0 text-sm">to</span>
            <input
              type="number"
              value={localFilters.priceRange[1]}
              onChange={(e) => handlePriceRangeChange(1, parseInt(e.target.value) || 1000000)}
              placeholder="Max"
              className="w-0 flex-1 min-w-0 p-2 border border-border rounded-industrial focus:ring-2 focus:ring-ring focus:border-transparent text-sm"
            />
          </div>
          <div className="text-xs text-text-secondary break-words">
            ${localFilters.priceRange[0].toLocaleString()} - ${localFilters.priceRange[1].toLocaleString()}
          </div>
        </div>
      </div>

      {/* Location */}
      <div>
        <label className="block text-sm font-heading font-bold uppercase tracking-wide text-foreground mb-2">
          Location
        </label>
        <select
          value={localFilters.location}
          onChange={(e) => handleFilterChange('location', e.target.value)}
          className="w-full p-2 border border-border rounded-industrial focus:ring-2 focus:ring-ring focus:border-transparent"
        >
          {locations.map((location) => (
            <option key={location} value={location}>
              {location}
            </option>
          ))}
        </select>
      </div>

      {/* Condition */}
      <div>
        <label className="block text-sm font-heading font-bold uppercase tracking-wide text-foreground mb-2">
          Condition
        </label>
        <select
          value={localFilters.condition}
          onChange={(e) => handleFilterChange('condition', e.target.value)}
          className="w-full p-2 border border-border rounded-industrial focus:ring-2 focus:ring-ring focus:border-transparent"
        >
          {conditions.map((condition) => (
            <option key={condition} value={condition}>
              {condition}
            </option>
          ))}
        </select>
      </div>

      {/* Manufacturer */}
      <div>
        <label className="block text-sm font-heading font-bold uppercase tracking-wide text-foreground mb-2">
          Manufacturer
        </label>
        <select
          value={localFilters.manufacturer}
          onChange={(e) => handleFilterChange('manufacturer', e.target.value)}
          className="w-full p-2 border border-border rounded-industrial focus:ring-2 focus:ring-ring focus:border-transparent"
        >
          {manufacturers.map((manufacturer) => (
            <option key={manufacturer} value={manufacturer}>
              {manufacturer}
            </option>
          ))}
        </select>
      </div>

      {/* Year Range */}
      <div>
        <label className="block text-sm font-heading font-bold uppercase tracking-wide text-foreground mb-2">
          Year Range
        </label>
        <div className="space-y-3">
          <div className="flex items-center space-x-2">
            <input
              type="number"
              value={localFilters.yearRange[0]}
              onChange={(e) => handleYearRangeChange(0, parseInt(e.target.value) || 1990)}
              min="1990"
              max="2024"
              className="flex-1 p-2 border border-border rounded-industrial focus:ring-2 focus:ring-ring focus:border-transparent"
            />
            <span className="text-text-secondary">to</span>
            <input
              type="number"
              value={localFilters.yearRange[1]}
              onChange={(e) => handleYearRangeChange(1, parseInt(e.target.value) || 2024)}
              min="1990"
              max="2024"
              className="flex-1 p-2 border border-border rounded-industrial focus:ring-2 focus:ring-ring focus:border-transparent"
            />
          </div>
        </div>
      </div>

      {/* Clear Filters */}
      <button
        onClick={clearAllFilters}
        className="w-full py-2 px-4 border border-border rounded-industrial text-text-secondary hover:bg-muted transition-industrial"
      >
        Clear All Filters
      </button>
    </div>
  );

  if (isMobile) {
    return (
      <div className={`fixed inset-0 z-50 lg:hidden ${isOpen ? 'block' : 'hidden'}`}>
        <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose} />
        <div className="fixed left-0 top-0 h-full w-80 bg-background border-r border-border overflow-y-auto">
          <div className="p-6">
            {panelContent}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-industrial p-6 industrial-shadow">
      {panelContent}
    </div>
  );
};

export default FilterPanel;